import { ReactNode } from 'react';

export type ColorSwatchSize = 'sm' | 'md' | 'lg';

export interface ColorSwatchProps {
  color: string;
  label?: ReactNode;
  /** Pokazuje hex pod swatchem. Default: false. */
  showHex?: boolean;
  size?: ColorSwatchSize;
  className?: string;
  onClick?: () => void;
}

const SIZE: Record<ColorSwatchSize, string> = {
  sm: 'w-6 h-6',
  md: 'w-10 h-10',
  lg: 'w-16 h-16',
};

/**
 * Kolorowy kwadrat preview - do color pickera, palette display, branding
 * preview. Checkerboard background pokazuje alpha.
 */
export function ColorSwatch({
  color,
  label,
  showHex = false,
  size = 'md',
  className,
  onClick,
}: ColorSwatchProps) {
  const interactive = Boolean(onClick);

  const swatch = (
    <span
      className={[
        'inline-block rounded-lg border border-border-strong shadow-sm',
        SIZE[size],
        interactive ? 'cursor-pointer hover:scale-105 transition-transform duration-fast' : '',
      ].filter(Boolean).join(' ')}
      style={{
        backgroundColor: color,
        backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.05) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.05) 75%), linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.05) 75%)',
        backgroundSize: '8px 8px',
        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
      }}
    >
      <span
        className={`block ${SIZE[size]} rounded-lg`}
        style={{ backgroundColor: color }}
      />
    </span>
  );

  if (!label && !showHex) {
    return onClick ? (
      <button type="button" onClick={onClick} className={className} aria-label={`Kolor ${color}`}>{swatch}</button>
    ) : (
      <span className={className}>{swatch}</span>
    );
  }

  return (
    <div
      className={['inline-flex flex-col items-center gap-1.5', className].filter(Boolean).join(' ')}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      {swatch}
      {label && <span className="text-xs text-text-secondary text-center">{label}</span>}
      {showHex && <code className="text-[10px] font-mono text-text-muted">{color}</code>}
    </div>
  );
}
